import React, { useEffect, useState, useRef } from 'react';
import { useAgeSignals } from '../hooks/useAgeSignals';
import { Capacitor } from '@capacitor/core';

/**
 * Age Gate Component for Texas SB 2420 Compliance
 * Wraps content that requires age verification
 */
export function AgeGate({ children, onRestricted }) {
  const { ageSignals, loading, checkAgeSignals, shouldRestrict, userStatus } = useAgeSignals();
  const [checked, setChecked] = useState(() => {
    // Initialize as checked for non-Android platforms
    return Capacitor.getPlatform() !== 'android';
  });
  const initRef = useRef(false);

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    
    // Only check on Android, and only once
    if (platform === 'android' && !initRef.current) {
      initRef.current = true;
      checkAgeSignals().finally(() => setChecked(true));
    }
  }, [checkAgeSignals]);

  // Still loading
  if (loading || !checked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60 text-sm">Verifying age status...</p>
        </div>
      </div>
    );
  }

  // Access denied - supervised user without approval
  if (shouldRestrict) {
    if (onRestricted) {
      onRestricted(ageSignals);
    }
    
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="glass-card p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h2 className="text-xl font-bold text-white mb-2">Access Restricted</h2>
          
          {userStatus === 'SUPERVISED_PENDING_APPROVAL' ? (
            <p className="text-white/60 mb-4">
              This app requires parental approval. Please ask your parent or guardian to approve access through Google Family Link.
            </p>
          ) : userStatus === 'SUPERVISED_APPROVAL_DENIED' ? (
            <p className="text-white/60 mb-4">
              Your parent or guardian has not approved access to this app. Please contact them if you believe this is a mistake.
            </p>
          ) : (
            <p className="text-white/60 mb-4">
              You do not have permission to access this content. This app is intended for users 18 and older.
            </p>
          )}
          
          <p className="text-white/40 text-xs">
            Status: {userStatus}
          </p>
        </div>
      </div>
    );
  }

  // Access granted
  return <>{children}</>;
}

export default AgeGate;
